import { useEffect, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { useTranslation } from 'react-i18next'
import { Button } from '@structural-climbing/ui'
import { Sheet } from '../../design/Sheet'
import { personColor } from '../family/personColor'

/** Someone whose check-in code can be shown: the member, or one of their children. */
export interface CodePerson {
  key: string
  /** Short, for the chip: "Me", or the child's first name. */
  label: string
  /** Full, beside the avatar: who the desk is about to scan. */
  name: string
  /** The plan line under the name: "Monthly · valid until 11/11/2026". */
  detail?: string
  /** The plan's status, as a pill on the right: "Active". */
  status?: string
  payload: string
}

interface Props {
  people: CodePerson[]
  /** The person on screen, or null when the sheet is closed. */
  current: string | null
  onSelect: (key: string) => void
  onClose: () => void
}

/**
 * The check-in code, large, for whoever is at the desk.
 *
 * A parent used to scroll to each child's card and open that child's code on
 * its own. Now one sheet holds everyone, with a chip per person, so the desk
 * scans the family one after another without the phone leaving the counter.
 *
 * White, with the code on its own white tile opted out of forced dark mode: a
 * browser that darkens the page would otherwise paint it grey on grey, and the
 * desk scanner could not read it.
 */
export function CodeOverlay({ people, current, onSelect, onClose }: Props) {
  const { t } = useTranslation()
  const found = people.find((p) => p.key === current) ?? null
  // Keep the last person while the sheet slides away, so it does not empty out
  // mid-animation.
  const [last, setLast] = useState<CodePerson | null>(found)
  useEffect(() => {
    if (found) setLast(found)
  }, [found])
  const person = found ?? last

  return (
    <Sheet
      isOpen={found !== null}
      onClose={onClose}
      aria-label={t('checkin.showToStaff')}
      maxHeight="calc(100dvh - 3.5rem)"
      className="h-[calc(100dvh-3.5rem)]"
      data-testid="code-sheet"
    >
      {person && (
        <div className="flex min-h-0 flex-1 flex-col items-center gap-4 overflow-y-auto px-5 pb-6 pt-1">
          {/* One switch for the whole family, full width, so the desk scans
              everyone without the phone leaving the counter. A segmented track
              here rather than chips: it is the only control on a screen held
              out at arm's length. */}
          {people.length > 1 && (
            <div
              role="tablist"
              aria-label={t('checkin.showToStaff')}
              className="flex w-full rounded-xl bg-[var(--gym-veil)] p-1"
            >
              {people.map((p) => {
                const on = p.key === person.key
                return (
                  <button
                    key={p.key}
                    type="button"
                    role="tab"
                    aria-selected={on}
                    onClick={() => onSelect(p.key)}
                    className={`min-w-0 flex-1 truncate rounded-lg px-2 py-2 text-sm font-semibold transition-colors ${
                      on ? 'bg-content1 text-foreground shadow-e1' : 'text-foreground/50'
                    }`}
                  >
                    {p.label}
                  </button>
                )
              })}
            </div>
          )}

          <div className="flex w-full items-center gap-3">
            <span
              aria-hidden
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-base font-bold text-white"
              style={{ backgroundColor: personColor(person.key) }}
            >
              {person.name.charAt(0).toUpperCase()}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-lg font-bold leading-tight">{person.name}</span>
              {person.detail && (
                // Wraps: a long plan name ("Abonament lunar cu instructor") was
                // cut off mid-word, and the desk reads this line.
                <span className="block break-words text-sm leading-snug text-foreground/50">
                  {person.detail}
                </span>
              )}
            </span>
            {person.status && (
              <span className="shrink-0 rounded-full bg-[var(--gym-primary)] px-3 py-1 text-sm font-bold text-[var(--gym-on-primary)]">
                {person.status}
              </span>
            )}
          </div>

          {/* The code fills the space left, as large as the screen allows. */}
          <div className="flex w-full flex-1 flex-col items-center justify-center gap-3">
            <button
              type="button"
              onClick={onClose}
              aria-label={t('checkin.tapToClose')}
              data-testid="code-tile"
              className="w-full max-w-[22rem] rounded-xl border border-[var(--gym-line)] bg-white p-5 [color-scheme:only_light]"
            >
              <QRCodeSVG
                key={person.key}
                value={person.payload}
                size={512}
                bgColor="#ffffff"
                fgColor="#000000"
                className="h-auto w-full"
              />
            </button>
            <p className="text-sm text-foreground/50">{t('checkin.showToStaff')}</p>
          </div>

          <Button
            variant="bordered"
            size="lg"
            className="w-full border-[var(--gym-line)] font-bold"
            onPress={onClose}
          >
            {t('checkin.done')}
          </Button>
        </div>
      )}
    </Sheet>
  )
}

/** The member's own code: their Auth0 id with `|` swapped for `.`, then the gym. */
export function memberCodePayload(userId: string, gymId: string): string {
  return `${userId.replace('|', '.')}/${gymId}`
}

/** A child's code: the parent's own payload with the child's id appended. */
export function childCodePayload(parentUserId: string, gymId: string, childId: string): string {
  return `${memberCodePayload(parentUserId, gymId)}/${childId}`
}
